import {
  DollarSignIcon,
  CreditCardIcon,
  TrendingUpIcon,
  CheckCircleIcon,
  FileTextIcon,
  BarChart3Icon,
  PlusIcon,
  XIcon,
  SaveIcon,
  Trash2Icon,
  AlertTriangleIcon,
  Loader2Icon,
  WalletIcon,
  ArrowUpCircleIcon,
  ArrowDownCircleIcon,
  ReceiptIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  ResponsivePageHeader,
  ResponsiveTabs,
  RotuloDoVoucherNoExtrato,
} from '@nebula/shared/components'
import { useHasPermission } from '@nebula/shared/hooks'
import {
  paymentMethodsApi,
  contractsApi,
  creditAccountApi,
  invoiceApi,
  type PaymentMethod,
  type Contract,
  type CreditBalance,
  type CreditTransaction,
  type InvoiceSummary,
  type InvoiceDetail,
} from '@nebula/shared/services'
import { rotuloDoTipoDeRecurso } from '@nebula/shared/utils'

import InvoiceLineContract from '../components/billing/InvoiceLineContract'
import SpecialContractDrawer from '../components/billing/SpecialContractDrawer'
import SpecialContractsSection from '../components/billing/SpecialContractsSection'

type TabId = 'overview' | 'invoices' | 'contracts' | 'payment' | 'credit'
const VALID_TABS = new Set<TabId>(['overview', 'invoices', 'contracts', 'payment', 'credit'])

const BillingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab: TabId = VALID_TABS.has(searchParams.get('tab') as TabId)
    ? (searchParams.get('tab') as TabId)
    : 'overview'
  const setActiveTab = useCallback(
    (tab: TabId) => {
      setSearchParams({ tab }, { replace: true })
    },
    [setSearchParams],
  )
  // #1091 F3: o contrato especial aberto mora na URL (`?tab=contracts&contrato=<id>`) — é o que
  // deixa a linha da fatura levar direto ao detalhe dele (K8)
  const contratoAberto = searchParams.get('contrato')
  const abrirContrato = useCallback(
    (id: string) => setSearchParams({ tab: 'contracts', contrato: id }, { replace: true }),
    [setSearchParams],
  )
  const fecharContrato = useCallback(
    () => setSearchParams({ tab: 'contracts' }, { replace: true }),
    [setSearchParams],
  )
  const canManageBilling = useHasPermission('billing:write')

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false)
  const [paymentMethodsError, setPaymentMethodsError] = useState<string | null>(null)

  // Contracts state
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loadingContracts, setLoadingContracts] = useState(false)
  const [contractsError, setContractsError] = useState<string | null>(null)

  // Credit account state
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null)
  const [creditTransactions, setCreditTransactions] = useState<CreditTransaction[]>([])
  const [loadingCredit, setLoadingCredit] = useState(false)
  const [creditError, setCreditError] = useState<string | null>(null)

  // Invoices state
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([])
  const [loadingInvoices, setLoadingInvoices] = useState(false)
  const [invoicesError, setInvoicesError] = useState<string | null>(null)
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null)
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false)

  // Modal states
  const [showAddCardModal, setShowAddCardModal] = useState(false)
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false)

  // Form state for add card (SEM gateway - vem automaticamente do backend)
  const [cardForm, setCardForm] = useState({
    type: 'credit_card',
    billing_mode: 'direct',
    card_last4: '',
    card_brand: 'visa',
    card_exp_month: '',
    card_exp_year: '',
    is_default: false,
  })

  // Loading states for actions
  const [creatingPaymentMethod, setCreatingPaymentMethod] = useState(false)
  const [settingDefault, setSettingDefault] = useState<string | null>(null)
  const [deactivating, setDeactivating] = useState<string | null>(null)

  // Load payment methods
  const loadPaymentMethods = async () => {
    setLoadingPaymentMethods(true)
    setPaymentMethodsError(null)

    try {
      const response = await paymentMethodsApi.listPaymentMethods()
      setPaymentMethods(response.payment_methods)
    } catch (error: any) {
      console.error('Error loading payment methods:', error)
      setPaymentMethodsError(
        error.response?.data?.detail || 'Erro ao carregar métodos de pagamento',
      )
    } finally {
      setLoadingPaymentMethods(false)
    }
  }

  // Load contracts
  const loadContracts = async () => {
    setLoadingContracts(true)
    setContractsError(null)

    try {
      const response = await contractsApi.listCustomerContracts()
      setContracts(response.contracts)
    } catch (error: any) {
      console.error('Error loading contracts:', error)
      setContractsError(error.response?.data?.detail || 'Erro ao carregar contratos')
    } finally {
      setLoadingContracts(false)
    }
  }

  // Load credit balance and transactions
  const loadCreditData = async () => {
    setLoadingCredit(true)
    setCreditError(null)

    try {
      const balance = await creditAccountApi.getCreditBalance()
      setCreditBalance(balance)

      if (balance.has_credit_account) {
        const transactions = await creditAccountApi.listCreditTransactions({ limit: 50 })
        setCreditTransactions(transactions)
      }
    } catch (error: any) {
      console.error('Error loading credit data:', error)
      setCreditError(error.response?.data?.detail || 'Erro ao carregar dados de crédito')
    } finally {
      setLoadingCredit(false)
    }
  }

  // Load invoices
  const loadInvoices = async () => {
    setLoadingInvoices(true)
    setInvoicesError(null)

    try {
      const response = await invoiceApi.listInvoices({ limit: 50 })
      setInvoices(response.invoices)
    } catch (error: any) {
      console.error('Error loading invoices:', error)
      setInvoicesError(error.response?.data?.detail || 'Erro ao carregar faturas')
    } finally {
      setLoadingInvoices(false)
    }
  }

  // Open invoice detail
  const openInvoiceDetail = async (invoiceId: string) => {
    setLoadingInvoiceDetail(true)
    try {
      const detail = await invoiceApi.getInvoice(invoiceId)
      setSelectedInvoice(detail)
    } catch (error: any) {
      console.error('Error loading invoice detail:', error)
      setInvoicesError(error.response?.data?.detail || 'Erro ao carregar a fatura')
    } finally {
      setLoadingInvoiceDetail(false)
    }
  }

  // Load data on mount
  useEffect(() => {
    loadPaymentMethods()
    loadContracts()
    loadCreditData()
    loadInvoices()
  }, [])

  // Create payment method (credit card)
  // IMPORTANTE: Gateway não é mais selecionado pelo cliente - vem automaticamente do backend
  const handleCreateCreditCard = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreatingPaymentMethod(true)

    try {
      // NOTA: Em produção, isso seria integrado com Asaas Checkout (white-label)
      // O asaas_customer_id e asaas_payment_method_id viriam do Asaas
      await paymentMethodsApi.createPaymentMethod({
        type: 'credit_card',
        billing_mode: 'direct',
        gateway: 'asaas',
        asaas_customer_id: `cus_mock_${Date.now()}`,
        asaas_payment_method_id: `pm_mock_${Date.now()}`,
        card_last4: cardForm.card_last4.slice(-4),
        card_brand: cardForm.card_brand,
        card_exp_month: Number.parseInt(cardForm.card_exp_month),
        card_exp_year: Number.parseInt(cardForm.card_exp_year),
        is_default: cardForm.is_default,
      })

      // Reload payment methods
      await loadPaymentMethods()

      // Close modal and reset form
      setShowAddCardModal(false)
      setCardForm({
        type: 'credit_card',
        billing_mode: 'direct',
        card_last4: '',
        card_brand: 'visa',
        card_exp_month: '',
        card_exp_year: '',
        is_default: false,
      })
    } catch (error: any) {
      console.error('Error creating payment method:', error)
      alert(error.response?.data?.detail || 'Erro ao adicionar cartão')
    } finally {
      setCreatingPaymentMethod(false)
    }
  }

  // Create payment method (invoice/postpaid)
  const handleCreateInvoiceMethod = async () => {
    setCreatingPaymentMethod(true)

    try {
      await paymentMethodsApi.createPaymentMethod({
        type: 'invoice',
        billing_mode: 'postpaid',
        is_default: paymentMethods.length === 0, // First one is default
      })

      // Reload payment methods
      await loadPaymentMethods()

      // Close modal
      setShowAddInvoiceModal(false)
    } catch (error: any) {
      console.error('Error creating invoice method:', error)
      alert(error.response?.data?.detail || 'Erro ao adicionar método de faturamento')
    } finally {
      setCreatingPaymentMethod(false)
    }
  }

  // Set payment method as default
  const handleSetDefault = async (paymentMethodId: string) => {
    setSettingDefault(paymentMethodId)

    try {
      await paymentMethodsApi.setDefaultPaymentMethod(paymentMethodId)
      await loadPaymentMethods()
    } catch (error: any) {
      console.error('Error setting default:', error)
      alert(error.response?.data?.detail || 'Erro ao definir método padrão')
    } finally {
      setSettingDefault(null)
    }
  }

  // Deactivate payment method
  const handleDeactivate = async (paymentMethodId: string) => {
    if (!confirm('Tem certeza que deseja remover este método de pagamento?')) {
      return
    }

    setDeactivating(paymentMethodId)

    try {
      await paymentMethodsApi.deactivatePaymentMethod(paymentMethodId)
      await loadPaymentMethods()
    } catch (error: any) {
      console.error('Error deactivating:', error)
      alert(error.response?.data?.detail || 'Erro ao remover método de pagamento')
    } finally {
      setDeactivating(null)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const getBillingModeLabel = (mode: string) => {
    return mode === 'direct' ? 'Direto (Cartão)' : 'Faturamento (Boleto/PIX)'
  }

  const getCardIcon = () => {
    return <CreditCardIcon className="size-6 text-gray-400" />
  }

  // Verifica se cliente tem billing postpaid (para mostrar aba de crédito)
  const hasPostpaidBilling = contracts.some((c) => c.billing_mode === 'postpaid')

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: BarChart3Icon },
    { id: 'invoices', label: 'Faturas', icon: ReceiptIcon },
    { id: 'contracts', label: 'Contratos', icon: FileTextIcon },
    { id: 'payment', label: 'Pagamento', icon: CreditCardIcon },
    ...(hasPostpaidBilling ? [{ id: 'credit', label: 'Saldo de Crédito', icon: WalletIcon }] : []),
  ]

  // Render overview tab
  const renderOverviewTab = () => {
    const totalMonthly = contracts.reduce((sum, c) => sum + c.total_monthly_cost, 0)
    const totalVMs = contracts.reduce((sum, c) => sum + c.active_vms, 0)

    return (
      <div className="space-y-6">
        {/* Metrics cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <div className="rounded-lg bg-white p-6 shadow">
            <div className="mb-4 flex items-center justify-between">
              <DollarSignIcon className="size-8 text-green-600" />
              <span className="text-sm text-gray-500">Custo Mensal</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalMonthly)}</div>
            <div className="text-sm text-gray-600">Total estimado</div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <div className="mb-4 flex items-center justify-between">
              <FileTextIcon className="size-8 text-blue-600" />
              <span className="text-sm text-gray-500">Contratos</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{contracts.length}</div>
            <div className="text-sm text-gray-600">Contratos ativos</div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <div className="mb-4 flex items-center justify-between">
              <TrendingUpIcon className="size-8 text-purple-600" />
              <span className="text-sm text-gray-500">VMs Ativas</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{totalVMs}</div>
            <div className="text-sm text-gray-600">Máquinas virtuais</div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow">
            <div className="mb-4 flex items-center justify-between">
              <CheckCircleIcon className="size-8 text-emerald-600" />
              <span className="text-sm text-gray-500">Status</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">Em dia</div>
            <div className="text-sm text-gray-600">Pagamentos</div>
          </div>
        </div>

        {/* Credit balance alert for postpaid customers */}
        {hasPostpaidBilling && creditBalance?.has_credit_account && (
          <div
            className={`rounded-lg p-6 shadow ${
              creditBalance.available_credit < creditBalance.low_balance_threshold
                ? 'border border-red-200 bg-red-50'
                : 'border border-green-200 bg-green-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <WalletIcon
                  className={`size-8 ${creditBalance.available_credit < creditBalance.low_balance_threshold ? 'text-red-600' : 'text-green-600'}`}
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Crédito Disponível</h3>
                  <p className="text-sm text-gray-600">
                    Conta pré-paga gerenciada pelo seu reseller
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-gray-900">
                  {creditAccountApi.formatCurrency(
                    creditBalance.available_credit,
                    creditBalance.currency,
                  )}
                </div>
                {creditBalance.available_credit < creditBalance.low_balance_threshold && (
                  <p className="mt-1 text-sm text-red-600">
                    ⚠️ Crédito disponível baixo - Entre em contato com seu reseller
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Ações Rápidas</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setActiveTab('payment')}
              className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
            >
              <PlusIcon className="size-4" />
              <span>Gerenciar Pagamentos</span>
            </button>
            <button
              onClick={() => setActiveTab('contracts')}
              className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              <FileTextIcon className="size-4" />
              <span>Ver Contratos</span>
            </button>
            {hasPostpaidBilling && (
              <button
                onClick={() => setActiveTab('credit')}
                className="flex items-center space-x-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
              >
                <WalletIcon className="size-4" />
                <span>Ver Saldo de Crédito</span>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Render contracts tab: os contratos especiais (#1091 F3) e os contratos de cobrança
  const renderContractsTab = () => (
    <div className="space-y-6">
      <SpecialContractsSection onAbrir={abrirContrato} />
      {renderBillingContracts()}
    </div>
  )

  const renderBillingContracts = () => {
    if (loadingContracts) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
          <span className="ml-2 text-gray-600">Carregando contratos...</span>
        </div>
      )
    }

    if (contractsError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center space-x-3">
            <AlertTriangleIcon className="size-6 text-red-600" />
            <div>
              <h3 className="font-medium text-red-900">Erro ao carregar contratos</h3>
              <p className="mt-1 text-red-700">{contractsError}</p>
            </div>
          </div>
        </div>
      )
    }

    if (contracts.length === 0) {
      return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <FileTextIcon className="mx-auto mb-4 size-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum contrato encontrado</h3>
          <p className="text-gray-600">Você não possui contratos ativos no momento.</p>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <div className="rounded-lg bg-white shadow">
          <div className="border-b p-6">
            <h2 className="text-lg font-semibold text-gray-900">Seus Contratos</h2>
          </div>
          <div>
            <div className="divide-y divide-gray-200 md:hidden">
              {contracts.map((contract) => (
                <div key={contract.id} className="space-y-2 p-4">
                  <div className="text-sm font-semibold text-gray-900">{contract.name}</div>
                  <div className="text-xs text-gray-500">ID: {contract.id.slice(0, 8)}...</div>
                  <div className="text-sm text-gray-700">Reseller: {contract.provider_name}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        contract.billing_mode === 'direct'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {getBillingModeLabel(contract.billing_mode)}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        contract.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <CheckCircleIcon className="mr-1 size-3" />
                      {contract.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700">VMs: {contract.active_vms}</div>
                  <div className="text-sm font-semibold text-gray-900">
                    Custo mensal: {formatCurrency(contract.total_monthly_cost)}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden overflow-x-auto md:block">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Contrato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Reseller
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Modo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      VMs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Custo Mensal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contracts.map((contract) => (
                    <tr key={contract.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{contract.name}</div>
                        <div className="text-xs text-gray-500">
                          ID: {contract.id.slice(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{contract.provider_name}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            contract.billing_mode === 'direct'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {getBillingModeLabel(contract.billing_mode)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{contract.active_vms}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {formatCurrency(contract.total_monthly_cost)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            contract.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <CheckCircleIcon className="mr-1 size-3" />
                          {contract.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render credit tab (NEW)
  const renderInvoicesTab = () => {
    // Carregando detalhe
    if (loadingInvoiceDetail) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Carregando fatura...</span>
        </div>
      )
    }

    // Visão de detalhe de uma fatura
    if (selectedInvoice) {
      const inv = selectedInvoice
      const isPrepaid = inv.billing_mode !== 'direct'
      return (
        <div className="space-y-6">
          <button
            onClick={() => setSelectedInvoice(null)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="mr-1 size-4" /> Voltar para faturas
          </button>

          {/* Cabeçalho da fatura */}
          <div className="rounded-lg bg-white p-6 shadow">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {invoiceApi.formatBillingPeriod(inv.billing_period)}
                  </h2>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${invoiceApi.getInvoiceStatusColor(
                      inv.status,
                    )}`}
                  >
                    {invoiceApi.getInvoiceStatusLabel(inv.status)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Fatura {inv.invoice_number}
                  {inv.issuer_name ? ` · ${inv.issuer_name}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">
                  {invoiceApi.getInvoiceAmountLabel(inv.billing_mode)}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
                </p>
              </div>
            </div>

            {/* Extrato de crédito (pré-pago) */}
            {isPrepaid && inv.opening_balance != null && (
              <div className="mt-4 grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-gray-500">Saldo inicial</p>
                  <p className="text-sm font-medium text-gray-900">
                    {invoiceApi.formatCurrency(inv.opening_balance, inv.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Recargas no período</p>
                  <p className="text-sm font-medium text-green-600">
                    +{invoiceApi.formatCurrency(inv.credits_added ?? 0, inv.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Saldo final</p>
                  <p className="text-sm font-medium text-gray-900">
                    {invoiceApi.formatCurrency(inv.closing_balance ?? 0, inv.currency)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Itens da fatura */}
          <div className="rounded-lg bg-white shadow">
            <div className="border-b p-6">
              <h3 className="text-base font-semibold text-gray-900">Itens do período</h3>
            </div>

            {inv.line_items.length === 0 ? (
              <div className="p-12 text-center">
                <FileTextIcon className="mx-auto mb-4 size-12 text-gray-400" />
                <p className="text-gray-600">Sem itens neste período.</p>
              </div>
            ) : (
              <div>
                {/* Mobile */}
                <div className="divide-y divide-gray-200 md:hidden">
                  {inv.line_items.map((li) => (
                    <div key={li.id} className="space-y-1 p-4">
                      {li.occurred_at && (
                        <div className="text-xs text-gray-500">
                          {creditAccountApi.formatDateTime(li.occurred_at)}
                        </div>
                      )}
                      <div className="text-sm text-gray-800">{li.description}</div>
                      <div className="text-xs text-gray-500">
                        {rotuloDoTipoDeRecurso(li.resource_type)}
                      </div>
                      <InvoiceLineContract linha={li} />
                      <div className="text-sm font-semibold text-gray-900">
                        {invoiceApi.formatCurrency(li.amount, li.currency)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Data
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Descrição
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Valor
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {inv.line_items.map((li) => (
                        <tr key={li.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                            {li.occurred_at ? creditAccountApi.formatDateTime(li.occurred_at) : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {li.description}
                            <InvoiceLineContract linha={li} />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {rotuloDoTipoDeRecurso(li.resource_type)}
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-semibold whitespace-nowrap text-gray-900">
                            {invoiceApi.formatCurrency(li.amount, li.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td
                          colSpan={3}
                          className="px-6 py-3 text-right text-sm font-medium text-gray-700"
                        >
                          Total
                        </td>
                        <td className="px-6 py-3 text-right text-sm font-bold whitespace-nowrap text-gray-900">
                          {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }

    // Visão de lista
    if (loadingInvoices) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Carregando faturas...</span>
        </div>
      )
    }

    if (invoicesError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center space-x-3">
            <AlertTriangleIcon className="size-6 text-red-600" />
            <div>
              <h3 className="font-medium text-red-900">Erro ao carregar faturas</h3>
              <p className="mt-1 text-red-700">{invoicesError}</p>
            </div>
          </div>
        </div>
      )
    }

    if (invoices.length === 0) {
      return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <ReceiptIcon className="mx-auto mb-4 size-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhuma fatura ainda</h3>
          <p className="text-gray-600">
            As faturas mensais aparecem aqui no fechamento de cada mês.
          </p>
        </div>
      )
    }

    return (
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">Faturas mensais</h2>
          <p className="mt-1 text-sm text-gray-600">
            Cada fatura consolida o consumo do mês — no pré-pago, descontado do seu crédito; no
            pós-pago, o valor a pagar.
          </p>
        </div>

        {/* Mobile */}
        <div className="divide-y divide-gray-200 md:hidden">
          {invoices.map((inv) => (
            <button
              key={inv.id}
              onClick={() => openInvoiceDetail(inv.id)}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {invoiceApi.formatBillingPeriod(inv.billing_period)}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${invoiceApi.getInvoiceStatusColor(
                      inv.status,
                    )}`}
                  >
                    {invoiceApi.getInvoiceStatusLabel(inv.status)}
                  </span>
                </div>
                <div className="text-xs text-gray-500">{inv.invoice_number}</div>
                <div className="text-sm font-semibold text-gray-900">
                  {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
                </div>
              </div>
              <ChevronRightIcon className="size-5 text-gray-400" />
            </button>
          ))}
        </div>

        {/* Desktop */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Fatura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Emissor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Valor
                </th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => openInvoiceDetail(inv.id)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                    {invoiceApi.formatBillingPeriod(inv.billing_period)}
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                    {inv.invoice_number}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{inv.issuer_name || '—'}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${invoiceApi.getInvoiceStatusColor(
                        inv.status,
                      )}`}
                    >
                      {invoiceApi.getInvoiceStatusLabel(inv.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-semibold whitespace-nowrap text-gray-900">
                    {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <ChevronRightIcon className="inline size-5 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderCreditTab = () => {
    if (loadingCredit) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-green-600" />
          <span className="ml-2 text-gray-600">Carregando dados de crédito...</span>
        </div>
      )
    }

    if (creditError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center space-x-3">
            <AlertTriangleIcon className="size-6 text-red-600" />
            <div>
              <h3 className="font-medium text-red-900">Erro ao carregar dados de crédito</h3>
              <p className="mt-1 text-red-700">{creditError}</p>
            </div>
          </div>
        </div>
      )
    }

    if (!creditBalance?.has_credit_account) {
      return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <WalletIcon className="mx-auto mb-4 size-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            Conta de crédito não disponível
          </h3>
          <p className="text-gray-600">
            Você não possui uma conta de crédito pré-paga configurada.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Entre em contato com seu reseller para mais informações.
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {/* Balance card */}
        <div className="rounded-xl bg-linear-to-br from-green-600 to-emerald-600 p-6 text-white shadow-lg">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="mb-1 text-sm text-green-100">Saldo Disponível</p>
              <div className="text-4xl font-bold">
                {creditAccountApi.formatCurrency(creditBalance.balance, creditBalance.currency)}
              </div>
            </div>
            <WalletIcon className="size-12 text-green-100" />
          </div>

          {creditBalance.credit_limit && (
            <div className="border-t border-green-400 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-green-100">Limite de Crédito:</span>
                <span className="font-semibold">
                  {creditAccountApi.formatCurrency(
                    creditBalance.credit_limit,
                    creditBalance.currency,
                  )}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-green-100">Crédito Disponível:</span>
                <span className="font-semibold">
                  {creditAccountApi.formatCurrency(
                    creditBalance.available_credit,
                    creditBalance.currency,
                  )}
                </span>
              </div>
            </div>
          )}

          {creditBalance.available_credit < creditBalance.low_balance_threshold && (
            <div className="bg-opacity-20 mt-4 rounded-lg border border-red-300 bg-red-500 p-3">
              <p className="text-sm font-medium">
                ⚠️ Crédito disponível baixo - Entre em contato com seu reseller para adicionar saldo
              </p>
            </div>
          )}
        </div>

        {/* Transactions */}
        <div className="rounded-lg bg-white shadow">
          <div className="border-b p-6">
            <h2 className="text-lg font-semibold text-gray-900">Histórico de Transações</h2>
            <p className="mt-1 text-sm text-gray-600">
              Visualize todas as movimentações em sua conta de crédito
            </p>
          </div>

          {creditTransactions.length === 0 ? (
            <div className="p-12 text-center">
              <FileTextIcon className="mx-auto mb-4 size-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhuma transação</h3>
              <p className="text-gray-600">Não há transações registradas ainda.</p>
            </div>
          ) : (
            <div>
              <div className="divide-y divide-gray-200 md:hidden">
                {creditTransactions.map((transaction) => (
                  <div key={transaction.id} className="space-y-2 p-4">
                    <div className="text-xs text-gray-500">
                      {creditAccountApi.formatDateTime(transaction.created_at)}
                    </div>
                    <div className="flex items-center space-x-2">
                      {transaction.type === 'credit' ? (
                        <ArrowUpCircleIcon className="size-4 text-green-600" />
                      ) : (
                        <ArrowDownCircleIcon className="size-4 text-red-600" />
                      )}
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${creditAccountApi.getTransactionTypeColor(
                          transaction.type,
                        )}`}
                      >
                        {creditAccountApi.getTransactionTypeLabel(transaction.type)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-800">{transaction.description}</div>
                    <RotuloDoVoucherNoExtrato
                      referenceType={transaction.reference_type}
                      competencia={transaction.competence_period}
                    />
                    <div
                      className={`text-sm font-semibold ${
                        transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {transaction.type === 'credit' ? '+' : '-'}
                      {creditAccountApi.formatCurrency(transaction.amount, transaction.currency)}
                    </div>
                    <div className="text-xs text-gray-600">
                      Saldo após:{' '}
                      {creditAccountApi.formatCurrency(
                        transaction.balance_after,
                        transaction.currency,
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Descrição
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Saldo Após
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {creditTransactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {creditAccountApi.formatDateTime(transaction.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {transaction.type === 'credit' ? (
                              <ArrowUpCircleIcon className="size-4 text-green-600" />
                            ) : (
                              <ArrowDownCircleIcon className="size-4 text-red-600" />
                            )}
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${creditAccountApi.getTransactionTypeColor(
                                transaction.type,
                              )}`}
                            >
                              {creditAccountApi.getTransactionTypeLabel(transaction.type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {transaction.description}
                          <RotuloDoVoucherNoExtrato
                            referenceType={transaction.reference_type}
                            competencia={transaction.competence_period}
                          />
                        </td>
                        <td
                          className={`px-6 py-4 text-right text-sm font-semibold ${
                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {transaction.type === 'credit' ? '+' : '-'}
                          {creditAccountApi.formatCurrency(
                            transaction.amount,
                            transaction.currency,
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                          {creditAccountApi.formatCurrency(
                            transaction.balance_after,
                            transaction.currency,
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Render payment tab
  const renderPaymentTab = () => {
    const renderPaymentMethodsList = () => {
      if (loadingPaymentMethods) {
        return (
          <div className="flex items-center justify-center py-12">
            <Loader2Icon className="size-8 animate-spin text-purple-600" />
            <span className="ml-2 text-gray-600">Carregando métodos de pagamento...</span>
          </div>
        )
      }

      if (paymentMethodsError) {
        return (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <div className="flex items-center space-x-3">
              <AlertTriangleIcon className="size-6 text-red-600" />
              <div>
                <h3 className="font-medium text-red-900">Erro ao carregar métodos de pagamento</h3>
                <p className="mt-1 text-red-700">{paymentMethodsError}</p>
              </div>
            </div>
          </div>
        )
      }

      if (paymentMethods.length === 0) {
        return (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <CreditCardIcon className="mx-auto mb-4 size-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum método de pagamento</h3>
            <p className="mb-4 text-gray-600">Adicione um método de pagamento para começar.</p>
            {canManageBilling && (
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowAddCardModal(true)}
                  className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
                >
                  Adicionar Cartão
                </button>
                <button
                  onClick={() => setShowAddInvoiceModal(true)}
                  className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                >
                  Faturamento (Boleto/PIX)
                </button>
              </div>
            )}
          </div>
        )
      }

      return (
        <div className="space-y-4">
          {paymentMethods.map((method) => (
            <div key={method.id} className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getCardIcon()}
                  <div>
                    {method.type === 'credit_card' && method.card_info ? (
                      <>
                        <div className="font-medium text-gray-900">
                          •••• •••• •••• {method.card_info.last4}
                        </div>
                        <div className="text-sm text-gray-500">
                          {method.card_info.brand} • Vence em {method.card_info.exp_month}/
                          {method.card_info.exp_year}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-medium text-gray-900">
                          {method.type === 'invoice' ? 'Faturamento (Boleto/PIX)' : method.type}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getBillingModeLabel(method.billing_mode)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {method.is_default && (
                    <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                      Principal
                    </span>
                  )}
                  {!method.is_default && canManageBilling && (
                    <button
                      onClick={() => handleSetDefault(method.id)}
                      disabled={settingDefault === method.id}
                      className="rounded-lg border border-purple-600 px-3 py-1 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
                    >
                      {settingDefault === method.id ? (
                        <Loader2Icon className="size-3 animate-spin" />
                      ) : (
                        'Definir como principal'
                      )}
                    </button>
                  )}
                  {canManageBilling && (
                    <button
                      onClick={() => handleDeactivate(method.id)}
                      disabled={deactivating === method.id}
                      className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      title="Remover"
                    >
                      {deactivating === method.id ? (
                        <Loader2Icon className="size-4 animate-spin" />
                      ) : (
                        <Trash2Icon className="size-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {canManageBilling && (
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowAddCardModal(true)}
                className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                + Adicionar Cartão
              </button>
              <button
                onClick={() => setShowAddInvoiceModal(true)}
                className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                + Adicionar Faturamento
              </button>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6">
          <div className="rounded-lg bg-white shadow">
            <div className="border-b p-6">
              <h2 className="text-lg font-semibold text-gray-900">Métodos de Pagamento</h2>
              <p className="mt-1 text-sm text-gray-600">
                Gerencie seus métodos de pagamento. O gateway é configurado automaticamente pelo seu
                reseller.
              </p>
            </div>
            <div className="p-6">{renderPaymentMethodsList()}</div>
          </div>
        </div>
      </div>
    )
  }

  // Add Card Modal (SEM seleção de gateway)
  const AddCardModal = () => (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <form onSubmit={handleCreateCreditCard}>
          <div className="flex items-center justify-between border-b p-6">
            <h3 className="text-lg font-semibold text-gray-900">Adicionar Cartão de Crédito</h3>
            <button
              type="button"
              onClick={() => setShowAddCardModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XIcon className="size-5" />
            </button>
          </div>

          <div className="space-y-4 p-6">
            {/* GATEWAY REMOVIDO - vem automaticamente do backend */}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Últimos 4 dígitos
              </label>
              <input
                type="text"
                required
                maxLength={4}
                value={cardForm.card_last4}
                onChange={(e) =>
                  setCardForm({ ...cardForm, card_last4: e.target.value.replace(/\D/g, '') })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                placeholder="1234"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Bandeira</label>
              <select
                value={cardForm.card_brand}
                onChange={(e) => setCardForm({ ...cardForm, card_brand: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
              >
                <option value="visa">Visa</option>
                <option value="mastercard">Mastercard</option>
                <option value="amex">American Express</option>
                <option value="elo">Elo</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Mês</label>
                <input
                  type="text"
                  required
                  maxLength={2}
                  value={cardForm.card_exp_month}
                  onChange={(e) =>
                    setCardForm({ ...cardForm, card_exp_month: e.target.value.replace(/\D/g, '') })
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  placeholder="12"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Ano</label>
                <input
                  type="text"
                  required
                  maxLength={4}
                  value={cardForm.card_exp_year}
                  onChange={(e) =>
                    setCardForm({ ...cardForm, card_exp_year: e.target.value.replace(/\D/g, '') })
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  placeholder="2025"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="default-card"
                checked={cardForm.is_default}
                onChange={(e) => setCardForm({ ...cardForm, is_default: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="default-card" className="text-sm text-gray-700">
                Definir como cartão principal
              </label>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-xs text-blue-800">
                ℹ️ Em produção, os dados do cartão seriam processados diretamente pelo gateway de
                pagamento configurado pelo seu reseller (Stripe/Asaas) para máxima segurança.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 border-t p-6">
            <button
              type="button"
              onClick={() => setShowAddCardModal(false)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={creatingPaymentMethod}
              className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {creatingPaymentMethod ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" /> <span>Salvando...</span>
                </>
              ) : (
                <>
                  <SaveIcon className="size-4" /> <span>Adicionar Cartão</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  // Add Invoice Modal
  const AddInvoiceModal = () => (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b p-6">
          <h3 className="text-lg font-semibold text-gray-900">Adicionar Faturamento (Postpaid)</h3>
          <button
            onClick={() => setShowAddInvoiceModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XIcon className="size-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
            <h4 className="mb-2 font-medium text-green-900">Faturamento Postpaid</h4>
            <p className="text-sm text-green-800">
              Com este método, você utiliza crédito pré-pago fornecido pelo seu reseller. O reseller
              gerencia seu saldo e pode enviá-lo faturas via:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-green-800">
              <li>• Boleto bancário</li>
              <li>• PIX</li>
              <li>• Transferência bancária</li>
            </ul>
            <p className="mt-2 text-sm text-green-800">
              O saldo de crédito será exibido na aba "Saldo de Crédito" após configurado.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 border-t p-6">
          <button
            onClick={() => setShowAddInvoiceModal(false)}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleCreateInvoiceMethod}
            disabled={creatingPaymentMethod}
            className="flex items-center space-x-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {creatingPaymentMethod ? (
              <>
                <Loader2Icon className="size-4 animate-spin" /> <span>Salvando...</span>
              </>
            ) : (
              <>
                <SaveIcon className="size-4" /> <span>Adicionar Faturamento</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Cobrança e Contratos"
        description="Gerencie seus contratos, métodos de pagamento e saldo de crédito"
        icon={DollarSignIcon}
        actions={
          <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
            <DollarSignIcon className="size-4" />
            <span>Centro de Cobrança</span>
          </div>
        }
      />

      {/* Tabs */}
      <div className="mb-6">
        <ResponsiveTabs
          tabs={tabs}
          value={activeTab}
          onChange={(tabId) => setActiveTab(tabId as TabId)}
          ariaLabel="Abas de cobrança"
        />
      </div>

      {/* Content */}
      <div>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'invoices' && renderInvoicesTab()}
        {activeTab === 'contracts' && renderContractsTab()}
        {activeTab === 'payment' && renderPaymentTab()}
        {activeTab === 'credit' && renderCreditTab()}
      </div>

      {/* Modals */}
      {activeTab === 'contracts' && contratoAberto && (
        <SpecialContractDrawer
          id={contratoAberto}
          onClose={fecharContrato}
          onVerFatura={(invoiceId) => {
            setActiveTab('invoices')
            void openInvoiceDetail(invoiceId)
          }}
        />
      )}
      {showAddCardModal && <AddCardModal />}
      {showAddInvoiceModal && <AddInvoiceModal />}
    </div>
  )
}

export default BillingPage
